import { NumbersOnlyDirective } from "./../../card-processing/numbers-only.directive";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  type OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { addHyphen, isEmailValid } from "src/app/core/constants/constants";
import { ErrorDialogComponent } from "src/app/core/error-dialog/error-dialog.component";
import { CollectionDetails } from "../collection-model";
import { PaymentService } from "../../shared/services/payment.service";
import { Subject, takeUntil } from "rxjs";
import { ResponseCodes } from "../../shared/models/bankPaymentRes";
import { DecimalPipe } from "@angular/common";

@Component({
  selector: "app-collection-details",
  imports: [ReactiveFormsModule, ErrorDialogComponent],
  providers: [DecimalPipe],
  templateUrl: "./collection-details.component.html",
  styleUrls: ["./collection-details.component.scss"],
})
export class CollectionDetailsComponent implements OnInit, AfterViewInit {
  fb = inject(FormBuilder);
  route = inject(ActivatedRoute);

  paymentService = inject(PaymentService);
  router = inject(Router);

  form: FormGroup;
  showModal: boolean;
  errorMessage: string = "";

  destroy$ = new Subject();
  details: CollectionDetails;

  rand: number;
  fee: string;

  decimalPipe = inject(DecimalPipe);
  cd = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.form = this.fb.group({
      reference: [{ value: "", disabled: true }],
      agencyName: [{ value: "", disabled: true }],
      amount: [{ value: "", disabled: true }],
      payerName: [{ value: "", disabled: true }],
      revName: [{ value: "", disabled: true }],
      amountToPay: [{ value: "", disabled: true }],
      phoneNumber: [""],
      email: [""],
    });

    this.route.queryParams.subscribe((param) => {
      if (param.referenceKey) {
        setTimeout(() => {
          this.paymentService
            .validateCollection(param.referenceKey)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                if (res.responseCode === ResponseCodes.success) {
                  this.details = res;
                  this.form.patchValue({
                    reference: param.referenceKey,
                    amountToPay: res.amount,
                    ...res,
                  });
                  if (res.paymentFlag === "PartPayment") {
                    this.form.get("amountToPay")?.enable();
                  }
                }
                if (res.responseCode === ResponseCodes.error) {
                  this.showModal = true;
                  this.errorMessage = res.responseDescription;
                  this.cd.detectChanges();
                }
              },
            });
        }, 1000);
      } else {
        this.router.navigate(["/payment/collection"]);
      }
    });
  }

  getRand() {
    this.rand = Math.floor(Math.random() * 900);
    return `${Date.now().toString()}${this.rand}`;
  }

  submit() {
    let val = this.form.getRawValue();
    if (!val.amountToPay || val.amountToPay === "0.00") {
      this.showModal = true;
      this.errorMessage = "Amount to pay is required";
      return;
    }

    if (!val.phoneNumber) {
      this.showModal = true;
      this.errorMessage = "Phone number is required";
      return;
    }
    if (val.phoneNumber.length !== 11) {
      this.showModal = true;
      this.errorMessage = "Phone number is invalid";
      return;
    }
    if (!val.email) {
      this.showModal = true;
      this.errorMessage = "Email is required";
      return;
    }
    if (!isEmailValid(val.email)) {
      this.showModal = true;
      this.errorMessage = "Email is invalid";
    }
    const payload = {
      reference: this.getRand(),
      userId: val.email,
      amount: val?.amountToPay.replace(/,/g, ""),
      currency: "NGN",
      clientId: this.details.clientId,
      feeBearer: "CUSTOMER",
      metadata: {
        email: val.email,
        phoneNumber: val.phoneNumber,
        billReference: val.reference,
        creditAccount: this.details.creditAccount,
        amount: val?.amountToPay.replace(/,/g, ""),
        subType: "collection",
        fees: this.fee,
      },
      settlementInfo: {
        bankCode: this.details.cbnCode,
        accountNumber: this.details.creditAccount,
      },
    };
    payload["callbackUrl"] =
      `${window.location.origin}/payment/receipt?payRef=${payload.reference}`;
    this.paymentService
      .createPayment(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.router.navigate([`/pay/${res["externalPaymentReference"]}`]);
        },
      });
  }

  validateInput(evt) {
    return evt.charCode >= 43 && evt.charCode <= 57;
  }

  formatInput(evt) {
    const amount = evt.target.value;
    const amt = this.decimalPipe.transform(amount.replace(/,/g, ""), "1.2-2");
    this.form.controls["amountToPay"].setValue(amt);
    // console.log(evt.target.value);
  }

  removeCommas(evt) {
    const amount = evt.target.value;
    this.form.controls["amountToPay"].setValue(amount.replace(/,/g, ""));
  }

  ngAfterViewInit(): void {
    this.form.controls["amountToPay"].valueChanges.subscribe((amount) => {
      const amt = amount.replace(/,/g, "");
      this.fee = (amt * 0.01).toFixed(2);
      if (+this.fee > 1500) {
        this.fee = "1500";
      }
    });
  }
}
